import { encodeJpegWithinBudget } from '@/utils/compress-jpeg';
import { measureSharpness } from '@/utils/sharpness';

declare global {
  interface MediaTrackCapabilities {
    torch?: boolean;
  }
  interface MediaTrackConstraintSet {
    torch?: boolean;
  }
}

export function getCurrentOrientationAngle(): number {
  return (
    screen.orientation?.angle ?? (typeof window.orientation === 'number' ? window.orientation : 0)
  );
}

/** Null until the stream reports its frame size; drawing before that would give an empty photo. */
function createCanvasFromVideo(video: HTMLVideoElement, rotation: number) {
  const { videoWidth: width, videoHeight: height } = video;
  if (!width || !height) return null;
  const swap = Math.abs(rotation) % 180 === 90;
  const canvas = document.createElement('canvas');
  canvas.width = swap ? height : width;
  canvas.height = swap ? width : height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(video, -width / 2, -height / 2, width, height);
  return canvas;
}

/**
 * Rotation still needed to make a frame upright. Browsers that already deliver the stream in the
 * screen's orientation need none; only a sensor-oriented stream (portrait frames while the screen
 * is landscape, or vice versa) is turned by the screen angle.
 */
function getFrameRotation(video: HTMLVideoElement, screenAngle: number): number {
  if (!video.videoWidth || !video.videoHeight) return 0;
  const isFrameLandscape = video.videoWidth >= video.videoHeight;
  const isScreenLandscape = Math.abs(screenAngle) % 180 === 90;
  return isFrameLandscape === isScreenLandscape ? 0 : screenAngle;
}

/** Stream size for still photos taken from the video feed; devices without 4K fall back on their own. */
export const PHOTO_STREAM_SIZE = { width: { ideal: 3840 }, height: { ideal: 2160 } };

export type CapturedFrame = {
  file: File;
  /** Focus score of the frame; compare with SHARPNESS_THRESHOLD. */
  sharpness: number;
};

const BURST_FRAME_COUNT = 3;
const BURST_INTERVAL_MS = 120;

/**
 * Grabs a few frames in quick succession, upright for the given screen rotation, and keeps the
 * sharpest, so a shaky moment at the shutter press does not decide the photo. Only the winner is
 * encoded as JPEG.
 */
export async function captureSharpestVideoFrame(
  video: HTMLVideoElement,
  screenAngle: number,
): Promise<CapturedFrame | null> {
  const rotation = getFrameRotation(video, screenAngle);
  let best: { canvas: HTMLCanvasElement; sharpness: number } | null = null;
  for (let attempt = 0; attempt < BURST_FRAME_COUNT; attempt++) {
    if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, BURST_INTERVAL_MS));
    const canvas = createCanvasFromVideo(video, rotation);
    if (!canvas) continue;
    const sharpness = measureSharpness(canvas);
    if (!best || sharpness > best.sharpness) best = { canvas, sharpness };
  }
  if (!best) return null;
  const file = await encodeJpegWithinBudget(best.canvas, best.canvas.width, best.canvas.height);
  return { file, sharpness: best.sharpness };
}
